import React, { useState } from 'react';
import { secureFetch } from './common';

const DENTAL_API_URL = process.env.REACT_APP_API_URL || 'https://dentalexpressserver.azurewebsites.net';

const OrgRegistration = () => {
  const [email, setEmail] = useState('');
  const [orgIdentifier, setOrgIdentifier] = useState('');
  const [status, setStatus] = useState('');
  const [submitting, setSubmitting] = useState(false);
  const [result, setResult] = useState(null);

  // Email recipients — auto-populated from quote, editable
  const [customerEmails, setCustomerEmails] = useState([]);
  const [repEmails, setRepEmails] = useState([]);
  const [newCustomerEmail, setNewCustomerEmail] = useState('');
  const [newRepEmail, setNewRepEmail] = useState('');
  const [sendingEmail, setSendingEmail] = useState(false);
  const [emailStatus, setEmailStatus] = useState('');

  const fetchQuoteEmails = async (organizationId) => {
    try {
      const res = await secureFetch(`${DENTAL_API_URL}/api/quotes?organizationId=${encodeURIComponent(organizationId)}`);
      if (!res.ok) return;
      const data = await res.json();
      const quotes = data.quotes || data || [];
      // Find most recent accepted quote, or fall back to any quote
      const quote = quotes.find(q => q.status === 'accepted') || quotes[0];
      if (!quote) return;

      const custEmails = new Set();
      const rEmails = new Set();
      if (quote.billingEmail) custEmails.add(quote.billingEmail.toLowerCase());
      if (quote.dealOwnerEmail) rEmails.add(quote.dealOwnerEmail.toLowerCase());

      return { customerEmails: [...custEmails], repEmails: [...rEmails] };
    } catch {
      // Non-critical — quote lookup is best-effort
      return null;
    }
  };

  const generateLink = async (e) => {
    e.preventDefault();

    if (!email || !orgIdentifier) {
      setStatus('Please enter both email and organization name or ID');
      return;
    }

    try {
      setSubmitting(true);
      setStatus('Looking up organization…');
      setResult(null);
      setEmailStatus('');

      const token = localStorage.getItem('jwtToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const isOrgId = orgIdentifier.trim().startsWith('org-');
      const body = {
        email,
        bSendEmail: false,
        ...(isOrgId
          ? { organizationId: orgIdentifier.trim() }
          : { organizationName: orgIdentifier.trim() }),
      };

      const response = await fetch(`${DENTAL_API_URL}/api/send-org-registration`, {
        method: 'POST',
        headers,
        body: JSON.stringify(body),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'An unknown error occurred' }));
        throw new Error(errorData.message);
      }

      const data = await response.json();
      setResult(data);
      setStatus('');

      // Auto-populate emails from quote data
      const custSet = new Set([email.toLowerCase()]);
      const repSet = new Set();

      const quoteEmails = await fetchQuoteEmails(data.organizationId);
      if (quoteEmails) {
        quoteEmails.customerEmails.forEach(e => custSet.add(e));
        quoteEmails.repEmails.forEach(e => repSet.add(e));
      }

      setCustomerEmails([...custSet]);
      setRepEmails([...repSet]);
    } catch (error) {
      setStatus(`Error: ${error.message}`);
      setResult(null);
    } finally {
      setSubmitting(false);
    }
  };

  const addEmail = (list, setList, newVal, setNewVal) => {
    const trimmed = newVal.trim().toLowerCase();
    if (trimmed && !list.includes(trimmed)) {
      setList([...list, trimmed]);
    }
    setNewVal('');
  };

  const removeEmail = (list, setList, emailToRemove) => {
    setList(list.filter(e => e !== emailToRemove));
  };

  const sendOnboardingEmail = async () => {
    if (!result) return;
    if (customerEmails.length === 0) {
      setEmailStatus('Error: At least one customer email is required');
      return;
    }
    try {
      setSendingEmail(true);
      setEmailStatus('');

      const token = localStorage.getItem('jwtToken');
      const headers = { 'Content-Type': 'application/json' };
      if (token) headers.Authorization = `Bearer ${token}`;

      const response = await fetch(`${DENTAL_API_URL}/api/send-onboarding-instructions`, {
        method: 'POST',
        headers,
        body: JSON.stringify({
          recipientEmail: customerEmails,
          ccEmail: repEmails.length > 0 ? repEmails : null,
          organizationName: result.organizationName,
          registrationUrl: result.registrationUrl,
          practices: result.practices,
        }),
      });

      if (!response.ok) {
        const errorData = await response.json().catch(() => ({ message: 'Unknown error' }));
        throw new Error(errorData.message);
      }

      setEmailStatus('success');
    } catch (error) {
      setEmailStatus(`Error: ${error.message}`);
    } finally {
      setSendingEmail(false);
    }
  };

  const isError = status.startsWith('Error') || status.startsWith('Please');

  const schedulingLink = 'https://calendar.app.google/skHMydPQ9u6zGTHv8';

  return (
    <div style={{ maxWidth: 800, margin: '0 auto', padding: '30px 20px', fontFamily: '-apple-system, BlinkMacSystemFont, "Segoe UI", Roboto, sans-serif' }}>
      <h1 style={{ fontSize: 28, fontWeight: 700, marginBottom: 6, color: '#1a1a1a' }}>Send Onboarding Instructions</h1>
      <p style={{ color: '#666', marginBottom: 30, fontSize: 14 }}>
        Look up an organization, then send onboarding instructions automatically or share them manually.
      </p>

      <form onSubmit={generateLink}>
        <div style={{ display: 'flex', gap: 12, marginBottom: 14 }}>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Email Address<Req /></label>
            <input type="email" value={email} onChange={(e) => setEmail(e.target.value)} placeholder="admin@example.com" style={inputStyle} />
          </div>
          <div style={{ flex: 1 }}>
            <label style={labelStyle}>Organization Name or ID<Req /></label>
            <input type="text" value={orgIdentifier} onChange={(e) => setOrgIdentifier(e.target.value)} placeholder="Smile Dental or org-1234567890" style={inputStyle} />
          </div>
        </div>

        {status && (
          <div style={{
            background: isError ? '#fef2f2' : '#f8fafc',
            border: `1px solid ${isError ? '#fecaca' : '#e2e8f0'}`,
            borderRadius: 8, padding: '10px 16px', marginBottom: 14,
            color: isError ? '#dc2626' : '#475569', fontSize: 14,
          }}>{status}</div>
        )}

        <button type="submit" disabled={submitting} style={buttonStyle(submitting)}>
          {submitting ? 'Looking up…' : 'Look Up Organization'}
        </button>
      </form>

      {result && (
        <div style={{ marginTop: 30 }}>
          {/* Org Summary */}
          <Section title="Organization Summary">
            <InfoRow label="Organization" value={result.organizationName} />
            <InfoRow label="Org ID" value={result.organizationId} />
            <InfoRow label="Practices" value={`${result.practices.length} location(s)`} />
          </Section>

          {/* ─── OPTION A: Automated Email ─── */}
          <Section title="Option A — Send Automated Email">
            <p style={{ fontSize: 13, color: '#666', marginBottom: 14 }}>
              Send a branded onboarding email directly to the customer. It includes the scheduling link, registration link, PMS setup instructions, and phone porting details.
            </p>

            {/* Customer Emails (To) */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Customer Email(s)</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {customerEmails.map(ce => (
                  <EmailChip key={ce} email={ce} onRemove={() => removeEmail(customerEmails, setCustomerEmails, ce)} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  value={newCustomerEmail}
                  onChange={(e) => setNewCustomerEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(customerEmails, setCustomerEmails, newCustomerEmail, setNewCustomerEmail); } }}
                  placeholder="Add another customer email"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => addEmail(customerEmails, setCustomerEmails, newCustomerEmail, setNewCustomerEmail)}
                  style={{ ...copyBtnStyle, padding: '8px 16px' }}
                >
                  Add
                </button>
              </div>
            </div>

            {/* Rep Emails (CC) */}
            <div style={{ marginBottom: 14 }}>
              <label style={labelStyle}>Rep Email(s) — CC</label>
              <div style={{ display: 'flex', flexWrap: 'wrap', gap: 6, marginBottom: 6 }}>
                {repEmails.map(re => (
                  <EmailChip key={re} email={re} onRemove={() => removeEmail(repEmails, setRepEmails, re)} />
                ))}
              </div>
              <div style={{ display: 'flex', gap: 8 }}>
                <input
                  type="email"
                  value={newRepEmail}
                  onChange={(e) => setNewRepEmail(e.target.value)}
                  onKeyDown={(e) => { if (e.key === 'Enter') { e.preventDefault(); addEmail(repEmails, setRepEmails, newRepEmail, setNewRepEmail); } }}
                  placeholder="Add another rep email"
                  style={{ ...inputStyle, flex: 1 }}
                />
                <button
                  type="button"
                  onClick={() => addEmail(repEmails, setRepEmails, newRepEmail, setNewRepEmail)}
                  style={{ ...copyBtnStyle, padding: '8px 16px' }}
                >
                  Add
                </button>
              </div>
            </div>

            <button
              onClick={sendOnboardingEmail}
              disabled={sendingEmail || customerEmails.length === 0}
              style={{ ...buttonStyle(sendingEmail || customerEmails.length === 0), marginBottom: 12 }}
            >
              {sendingEmail ? 'Sending…' : 'Send Onboarding Email'}
            </button>

            {emailStatus === 'success' && (
              <div style={{ background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 8, padding: '10px 16px', fontSize: 14, color: '#16a34a' }}>
                Onboarding email sent to <strong>{customerEmails.join(', ')}</strong>
                {repEmails.length > 0 ? <> (CC: <strong>{repEmails.join(', ')}</strong>)</> : null}.
              </div>
            )}
            {emailStatus && emailStatus !== 'success' && (
              <div style={{ background: '#fef2f2', border: '1px solid #fecaca', borderRadius: 8, padding: '10px 16px', fontSize: 14, color: '#dc2626' }}>
                {emailStatus}
              </div>
            )}
          </Section>

          {/* ─── OPTION B: Manual Outreach ─── */}
          <Section title="Option B — Manual Outreach">
            <p style={{ fontSize: 13, color: '#666', marginBottom: 16 }}>
              If you prefer to reach out personally, copy the links and details below to include in your own email or message.
            </p>

            {/* Scheduling Link */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...labelStyle, fontWeight: 600 }}>1. Scheduling Link</label>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 6px' }}>Customer books their onboarding call here.</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" readOnly value={schedulingLink} style={{ ...inputStyle, flex: 1, background: '#f9fafb', fontFamily: 'monospace', fontSize: 12 }} onClick={(e) => e.target.select()} />
                <CopyBtn text={schedulingLink} />
              </div>
            </div>

            {/* Registration Link */}
            <div style={{ marginBottom: 16 }}>
              <label style={{ ...labelStyle, fontWeight: 600 }}>2. Registration Link</label>
              <p style={{ fontSize: 12, color: '#6b7280', margin: '0 0 6px' }}>Customer creates their Newton admin account — expires in 7 days.</p>
              <div style={{ display: 'flex', gap: 8 }}>
                <input type="text" readOnly value={result.registrationUrl} style={{ ...inputStyle, flex: 1, background: '#f9fafb', fontFamily: 'monospace', fontSize: 12 }} onClick={(e) => e.target.select()} />
                <CopyBtn text={result.registrationUrl} />
              </div>
            </div>

            {/* Per-Practice Details */}
            <label style={{ ...labelStyle, fontWeight: 600, marginBottom: 10, display: 'block' }}>3. Practice Setup Details</label>
            {result.practices.map((practice) => (
              <PracticeCard key={practice.practiceId} practice={practice} />
            ))}
          </Section>
        </div>
      )}
    </div>
  );
};

/* ─── Practice Card ───────────────────────────────────────────── */

const PracticeCard = ({ practice }) => {
  return (
    <div style={{ border: '1px solid #e5e7eb', borderRadius: 10, padding: 20, marginBottom: 20, background: '#fafafa' }}>
      <div style={{ display: 'flex', justifyContent: 'space-between', alignItems: 'flex-start', marginBottom: 14 }}>
        <div>
          <h3 style={{ fontSize: 16, fontWeight: 700, color: '#1a1a1a', margin: 0 }}>{practice.name}</h3>
          <p style={{ fontSize: 12, color: '#6b7280', margin: '2px 0 0' }}>{practice.practiceId}</p>
        </div>
        <div style={{ display: 'flex', gap: 6 }}>
          {practice.voip && <Badge color="blue">Phones</Badge>}
          {practice.inbound && <Badge color="emerald">Inbound</Badge>}
          {practice.outbound && <Badge color="purple">Outbound</Badge>}
        </div>
      </div>

      {practice.address && <InfoRow label="Address" value={
        typeof practice.address === 'object'
          ? [practice.address.streetAddress, practice.address.city, practice.address.state, practice.address.zip].filter(Boolean).join(', ')
          : practice.address
      } />}
      {practice.pms && <InfoRow label="PMS" value={practice.pms} />}

      {/* PMS Setup Instructions */}
      {practice.pms && <PmsInstructions practice={practice} />}

      {/* Phone Porting Info */}
      {practice.voip && <PhonePortingInfo practice={practice} />}
    </div>
  );
};

/* ─── PMS Instructions ────────────────────────────────────────── */

const PmsInstructions = ({ practice }) => {
  const pms = practice.pms;
  const integration = practice.pmsIntegration || {};

  return (
    <div style={{ marginTop: 16, padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 10 }}>
        PMS Setup: {pms}
      </h4>

      {(pms === 'Curve') && <CurveInstructions integration={integration} />}
      {(pms === 'OpenDental') && <OpenDentalInstructions integration={integration} />}
      {(pms === 'Dentrix' || pms === 'Eaglesoft' || pms === 'kolla') && <KollaInstructions pms={integration.pmsName || pms} integration={integration} />}
      {(pms === 'Denticon') && <DenticonInstructions />}
      {(pms === 'Ascend') && <AscendInstructions integration={integration} />}
    </div>
  );
};

const CurveInstructions = ({ integration }) => (
  <div>
    <div style={instructionBoxStyle('#eff6ff', '#bfdbfe')}>
      <p style={{ fontSize: 13, color: '#1e40af', margin: 0 }}>
        <strong>Important:</strong> The practice must set up Newton with a Curve account that has
        <strong> "Scheduler" level permission</strong>. They should contact their Curve administrator to create this account.
      </p>
    </div>
    <div style={{ marginTop: 10 }}>
      <p style={stepStyle}>1. Log in to Curve Dental as an admin</p>
      <p style={stepStyle}>2. Create a new user account for Newton with <strong>"Scheduler"</strong> permission level</p>
      <p style={stepStyle}>3. In Newton Settings → PMS Integration, enter the Curve username and password</p>
    </div>
    {integration.curveUsername && (
      <div style={{ marginTop: 10, padding: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
        <p style={{ fontSize: 12, color: '#16a34a', margin: 0 }}>Credentials already configured: <strong>{integration.curveUsername}</strong></p>
      </div>
    )}
  </div>
);

const OpenDentalInstructions = ({ integration }) => (
  <div>
    <div style={instructionBoxStyle('#eff6ff', '#bfdbfe')}>
      <p style={{ fontSize: 13, color: '#1e40af', margin: '0 0 8px' }}><strong>Setup Instructions:</strong></p>
      <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#1e40af' }}>
        <li style={{ marginBottom: 4 }}>In OpenDental, go to <strong>Main Menu → Setup → Advanced Setup → API</strong></li>
        <li style={{ marginBottom: 4 }}>Check <strong>Enabled</strong> to enable 2-way communication</li>
        <li style={{ marginBottom: 4 }}>Click <strong>Add Key</strong> and accept the license agreement</li>
        <li style={{ marginBottom: 4 }}>Copy the API key provided below and enter it in OpenDental when prompted</li>
      </ol>
      <a href="https://www.opendental.com/manual/fhir.html" target="_blank" rel="noopener noreferrer" style={{ fontSize: 12, color: '#2563eb', marginTop: 6, display: 'inline-block' }}>
        View full documentation →
      </a>
    </div>
    {integration.openDentalApiKey ? (
      <CopyableField label="OpenDental API Key" value={integration.openDentalApiKey} />
    ) : (
      <div style={{ marginTop: 10, padding: 10, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 6 }}>
        <p style={{ fontSize: 12, color: '#92400e', margin: 0 }}>No API key configured yet. The key will be generated during onboarding.</p>
      </div>
    )}
  </div>
);

const KollaInstructions = ({ pms, integration }) => (
  <div>
    <div style={instructionBoxStyle('#eff6ff', '#bfdbfe')}>
      <p style={{ fontSize: 13, color: '#1e40af', margin: '0 0 8px' }}>
        Newton uses <strong>Kolla</strong> to securely connect to {pms}. Kolla's HIPAA-compliant integration allows connection in seconds.
      </p>
    </div>
    <div style={instructionBoxStyle('#fef2f2', '#fecaca')}>
      <p style={{ fontSize: 13, color: '#991b1b', margin: 0 }}>
        <strong>Important:</strong> The Kolla link must be opened and installed <strong>directly on the server</strong> that runs the {pms} software — not on any other computer. Copy the link and paste it into the browser on the {pms} server machine.
      </p>
    </div>
    {integration.kollaLink ? (
      <CopyableField label="Kolla Installation Link" value={integration.kollaLink} />
    ) : (
      <div style={{ marginTop: 10, padding: 10, background: '#fefce8', border: '1px solid #fde68a', borderRadius: 6 }}>
        <p style={{ fontSize: 12, color: '#92400e', margin: 0 }}>No Kolla link configured yet. Please contact support to generate one.</p>
      </div>
    )}
  </div>
);

const DenticonInstructions = () => (
  <div>
    <div style={instructionBoxStyle('#eff6ff', '#bfdbfe')}>
      <p style={{ fontSize: 13, color: '#1e40af', margin: '0 0 8px' }}><strong>Setup Instructions:</strong></p>
      <p style={{ fontSize: 13, color: '#1e40af', margin: '0 0 6px' }}>
        To give Newton API access to Denticon, follow these steps directly in Denticon:
      </p>
      <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#1e40af' }}>
        <li style={{ marginBottom: 4 }}>Navigate to <strong>Setup → Offices → Vendor API Settings</strong></li>
        <li style={{ marginBottom: 4 }}>Click <strong>Add Vendor</strong></li>
        <li style={{ marginBottom: 4 }}>Select <strong>Newton AI</strong> from the vendor list</li>
        <li style={{ marginBottom: 4 }}>Activate the vendor</li>
      </ol>
      <p style={{ fontSize: 13, color: '#1e40af', margin: '8px 0 0' }}>
        Once activated, Newton will automatically connect. The practice should email <strong>support@joinnewton.com</strong> after completing these steps.
      </p>
    </div>
  </div>
);

const AscendInstructions = ({ integration }) => (
  <div>
    <div style={instructionBoxStyle('#eff6ff', '#bfdbfe')}>
      <p style={{ fontSize: 13, color: '#1e40af', margin: 0 }}>
        Enter the Dentrix Ascend <strong>Organization ID</strong> and <strong>Location ID</strong> in Newton Settings → PMS Integration to connect.
      </p>
    </div>
    {(integration.ascendOrgId || integration.ascendLocationId) && (
      <div style={{ marginTop: 10, padding: 10, background: '#f0fdf4', border: '1px solid #bbf7d0', borderRadius: 6 }}>
        {integration.ascendOrgId && <p style={{ fontSize: 12, color: '#16a34a', margin: '0 0 4px' }}>Org ID: <strong>{integration.ascendOrgId}</strong></p>}
        {integration.ascendLocationId && <p style={{ fontSize: 12, color: '#16a34a', margin: 0 }}>Location ID: <strong>{integration.ascendLocationId}</strong></p>}
      </div>
    )}
  </div>
);

/* ─── Phone Porting Info ──────────────────────────────────────── */

const PhonePortingInfo = ({ practice }) => {
  const migration = practice.phonesMigration || {};
  const portRequests = migration.portRequests || [];

  return (
    <div style={{ marginTop: 16, padding: 16, background: '#fff', border: '1px solid #e2e8f0', borderRadius: 8 }}>
      <h4 style={{ fontSize: 14, fontWeight: 600, color: '#1e293b', marginBottom: 10 }}>
        Phone Porting
      </h4>

      {portRequests.length > 0 ? (
        portRequests.map((req, i) => (
          <div key={req.id || i} style={{ padding: 12, background: '#f8fafc', border: '1px solid #e2e8f0', borderRadius: 6, marginBottom: 8 }}>
            <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: 8 }}>
              <span style={{ fontSize: 13, fontWeight: 600, color: '#374151' }}>Port Request #{i + 1}</span>
              <Badge color={req.portingStatus === 'complete' ? 'emerald' : req.portingStatus === 'submitted' ? 'blue' : 'amber'}>
                {req.portingStatus || 'pending'}
              </Badge>
            </div>
            {req.phoneNumbers?.length > 0 && (
              <InfoRow label="Phone Numbers" value={req.phoneNumbers.filter(Boolean).join(', ')} />
            )}
            {req.faxNumbers?.filter(Boolean).length > 0 && (
              <InfoRow label="Fax Numbers" value={req.faxNumbers.filter(Boolean).join(', ')} />
            )}
            {req.portDate && <InfoRow label="Target Port Date" value={req.portDate} />}
            {req.numberOfPhones && <InfoRow label="Desk Phones Needed" value={req.numberOfPhones} />}
            {req.billingPhone && <InfoRow label="Billing Phone (BTN)" value={req.billingPhone} />}
            <InfoRow label="LOA Signed" value={req.loaSigned ? 'Yes' : 'No'} />
          </div>
        ))
      ) : (
        <div>
          <div style={instructionBoxStyle('#fefce8', '#fde68a')}>
            <p style={{ fontSize: 13, color: '#92400e', margin: '0 0 8px' }}><strong>No port requests submitted yet.</strong> Once the practice registers and logs into the Newton portal, they will be guided through providing:</p>
            <ol style={{ margin: 0, paddingLeft: 20, fontSize: 13, color: '#92400e' }}>
              <li style={{ marginBottom: 4 }}>All phone numbers and fax numbers to port</li>
              <li style={{ marginBottom: 4 }}>Account PIN from their current phone provider (they are required by FCC to share this)</li>
              <li style={{ marginBottom: 4 }}>Account number from their current phone provider</li>
              <li style={{ marginBottom: 4 }}>Billing telephone number (BTN) — the main number on their phone bill</li>
              <li style={{ marginBottom: 4 }}>Desired port date (must be at least 1 week out — timing depends on current provider)</li>
              <li style={{ marginBottom: 4 }}>Number of physical desk phones needed</li>
              <li style={{ marginBottom: 4 }}>Most recent phone bill (PDF upload)</li>
              <li style={{ marginBottom: 4 }}>Letter of Authorization (LOA) — must be signed by the <strong>authorized signer on the current phone account</strong></li>
            </ol>
            <p style={{ fontSize: 12, color: '#78350f', margin: '8px 0 0', fontStyle: 'italic' }}>
              All of this is completed inside the Newton portal during onboarding — no action needed outside the app.
            </p>
          </div>
        </div>
      )}
    </div>
  );
};

/* ─── Copy Button (self-contained) ───────────────────────────── */

const CopyBtn = ({ text }) => {
  const [didCopy, setDidCopy] = useState(false);
  const handleCopy = () => {
    navigator.clipboard.writeText(text);
    setDidCopy(true);
    setTimeout(() => setDidCopy(false), 2000);
  };
  return (
    <button onClick={handleCopy} style={{ ...copyBtnStyle, minWidth: 80 }}>
      {didCopy ? 'Copied!' : 'Copy'}
    </button>
  );
};

/* ─── Email Chip ─────────────────────────────────────────────── */

const EmailChip = ({ email, onRemove }) => (
  <span style={{
    display: 'inline-flex', alignItems: 'center', gap: 6,
    background: '#f1f5f9', border: '1px solid #cbd5e1', borderRadius: 9999,
    padding: '4px 10px', fontSize: 13, color: '#334155',
  }}>
    {email}
    <button
      onClick={onRemove}
      style={{
        background: 'none', border: 'none', cursor: 'pointer',
        color: '#94a3b8', fontSize: 16, lineHeight: 1, padding: 0,
        fontWeight: 700,
      }}
      title="Remove"
    >
      &times;
    </button>
  </span>
);

/* ─── Reusable components ─────────────────────────────────────── */

const CopyableField = ({ label, value }) => {
  const [fieldCopied, setFieldCopied] = useState(false);

  const handleCopy = () => {
    navigator.clipboard.writeText(value);
    setFieldCopied(true);
    setTimeout(() => setFieldCopied(false), 2000);
  };

  return (
    <div style={{ marginTop: 10 }}>
      <label style={{ fontSize: 12, fontWeight: 600, color: '#374151', display: 'block', marginBottom: 4 }}>{label}</label>
      <div style={{ display: 'flex', gap: 8 }}>
        <input type="text" readOnly value={value} style={{ flex: 1, padding: '6px 10px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 12, fontFamily: 'monospace', background: '#f9fafb', boxSizing: 'border-box' }} onClick={(e) => e.target.select()} />
        <button onClick={handleCopy} style={copyBtnStyle}>
          {fieldCopied ? 'Copied!' : 'Copy'}
        </button>
      </div>
    </div>
  );
};

const Section = ({ title, children }) => (
  <div style={{ marginBottom: 24 }}>
    <h2 style={{ fontSize: 16, fontWeight: 600, color: '#374151', marginBottom: 12, paddingBottom: 8, borderBottom: '1px solid #e5e7eb' }}>{title}</h2>
    {children}
  </div>
);

const InfoRow = ({ label, value }) => (
  <div style={{ display: 'flex', fontSize: 13, marginBottom: 4 }}>
    <span style={{ color: '#6b7280', minWidth: 140, fontWeight: 500 }}>{label}</span>
    <span style={{ color: '#1a1a1a' }}>{value}</span>
  </div>
);

const Badge = ({ color, children }) => {
  const colors = {
    blue: { bg: '#eff6ff', text: '#1d4ed8', border: '#bfdbfe' },
    emerald: { bg: '#ecfdf5', text: '#047857', border: '#a7f3d0' },
    purple: { bg: '#f5f3ff', text: '#6d28d9', border: '#c4b5fd' },
    amber: { bg: '#fffbeb', text: '#92400e', border: '#fde68a' },
  };
  const c = colors[color] || colors.blue;
  return (
    <span style={{ fontSize: 11, fontWeight: 600, padding: '2px 8px', borderRadius: 9999, background: c.bg, color: c.text, border: `1px solid ${c.border}`, textTransform: 'capitalize' }}>
      {children}
    </span>
  );
};

const Req = () => <span style={{ color: '#dc2626', marginLeft: 2 }}>*</span>;

/* ─── Styles ──────────────────────────────────────────────────── */

const labelStyle = { display: 'block', fontSize: 13, fontWeight: 500, color: '#374151', marginBottom: 4 };
const inputStyle = { width: '100%', padding: '8px 12px', border: '1px solid #d1d5db', borderRadius: 6, fontSize: 14, outline: 'none', boxSizing: 'border-box' };
const buttonStyle = (disabled) => ({
  background: disabled ? '#9ca3af' : '#1E6434',
  color: '#fff', border: 'none', borderRadius: 8,
  padding: '12px 32px', fontSize: 15, fontWeight: 600,
  cursor: disabled ? 'not-allowed' : 'pointer', width: '100%',
});
const copyBtnStyle = { padding: '6px 16px', background: '#1E6434', color: '#fff', border: 'none', borderRadius: 6, cursor: 'pointer', fontWeight: 600, fontSize: 12, whiteSpace: 'nowrap' };
const instructionBoxStyle = (bg, border) => ({ padding: 12, background: bg, border: `1px solid ${border}`, borderRadius: 6, marginBottom: 10 });
const stepStyle = { fontSize: 13, color: '#374151', margin: '0 0 4px', paddingLeft: 4 };

export default OrgRegistration;
